<template>
  <div class="column">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.FILTERS.TITLE')"
    >
      <p>{{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.FILTERS.SUB_TITLE') }}</p>
    </woot-modal-header>
    <div class="row modal-content">
      <div class="small-12 medium-12 columns filters-wrap">
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-attributes="filterAttributes"
          :input-type="getInputType(appliedFilters[i].attribute_key)"
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :v="$v.appliedFilters.$each[i]"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />
        <div class="filter-actions">
          <woot-button
            icon="add"
            variant="smooth"
            size="small"
            @click="appendNewFilter"
          >
            {{ $t('CONTACTS_FILTER.ADD_NEW_FILTER') }}
          </woot-button>
          <woot-button
            icon="subtract"
            color-scheme="alert"
            variant="smooth"
            size="small"
            @click="clearFilters"
          >
            {{ $t('CONTACTS_FILTER.CLEAR_ALL_FILTERS') }}
          </woot-button>
        </div>
      </div>

      <div class="small-12 medium-12 columns">
        <div class="modal-footer justify-content-end w-full">
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('CONTACTS_FILTER.CANCEL_BUTTON_LABEL') }}
          </woot-button>
          <woot-button @click="submitFilterQuery">
            {{ $t('CONTACTS_FILTER.SUBMIT_BUTTON_LABEL') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from 'components/widgets/FilterInput/Index.vue';
import { AUDIENCE_CALL_STATUS } from '../broadcastAgent/aiCampaignConstant';
import filterMixin from 'shared/mixins/filterMixin';

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    filterItems: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    filterAttributes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: [
        {
          attribute_key: 'contact_name',
          filter_operator: 'contains',
          values: '',
          query_operator: 'and',
        },
      ],
      filterTypes: this.filterItems,
      filterGroups: [],
    };
  },
  validations: {
    appliedFilters: {
      $each: {
        values: {
          required: requiredIf(prop => {
            return (
              prop.filter_operator !== 'is_present' &&
              prop.filter_operator !== 'is_not_present'
            );
          }),
        },
      },
    },
  },
  watch: {
    initialAppliedFilters: {
      handler(newVal) {
        if (newVal?.length) {
          // Clone filters and re-match dropdown values by ID
          this.appliedFilters = newVal.map(filter => {
            const dropdownValues = this.getDropdownValues(filter.attribute_key);
            if (
              dropdownValues &&
              filter.values &&
              typeof filter.values === 'object'
            ) {
              // Re-match dropdown value by ID
              const matchedValue = dropdownValues.find(
                option => option.id === filter.values.id
              );
              if (matchedValue) {
                return {
                  ...filter,
                  values: matchedValue,
                };
              }
            }

            // For call_status, if values is a string (ID), find the matching object
            if (
              filter.attribute_key === 'call_status' &&
              typeof filter.values[0] === 'string'
            ) {
              const matchedValue = dropdownValues?.find(
                option => option.id === filter.values[0]
              );
              if (matchedValue) {
                return {
                  ...filter,
                  values: matchedValue,
                };
              }
            }

            return { ...filter };
          });

          const lastFilterIndex = this.appliedFilters.length - 1;
          if (
            this.appliedFilters[lastFilterIndex] &&
            !this.appliedFilters[lastFilterIndex].query_operator
          ) {
            this.appliedFilters[lastFilterIndex].query_operator = 'and';
          }
        } else {
          // Reset to default filter when no filters applied
          this.appliedFilters = [
            {
              attribute_key: 'contact_name',
              filter_operator: 'contains',
              values: '',
              query_operator: 'and',
            },
          ];
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Initial load is handled by the watcher with immediate: true
  },
  methods: {
    submitFilterQuery() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        console.log('Validation failed');
        return;
      }

      const formattedFilters = this.appliedFilters.map(el => {
        const temp = { ...el };
        if (typeof el.values === 'object' && el.values?.id) {
          temp.values = [el.values.id];
        } else if (Array.isArray(el.values)) {
          temp.values = [...el.values];
        } else {
          temp.values = el.values ? [el.values] : '';
        }

        return temp;
      });

      this.$emit('applyFilter', formattedFilters);
    },
    getInputType(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      switch (type) {
        case 'call_status':
          return AUDIENCE_CALL_STATUS.map(el => ({
            id: el.id,
            name: this.$t(el.title),
            title: this.$t(el.title),
          }));
        default:
          return undefined;
      }
    },
    showUserInput(operatorType) {
      return !(
        operatorType === 'is_present' || operatorType === 'is_not_present'
      );
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('CONTACTS_FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    appendNewFilter() {
      this.appliedFilters.push({
        attribute_key: 'contact_name',
        filter_operator: 'contains',
        values: '',
        query_operator: 'and',
      });
    },
    clearFilters() {
      this.$emit('clearFilters');
      this.onClose();
    },
  },
};
</script>

<style></style>
